import React, { useState, useEffect } from "react";
import { Modal, Tag, Button, Empty } from "antd";
import { ClockCircleOutlined, CarOutlined, ShoppingOutlined, CopyOutlined } from "@ant-design/icons";
import NumberFormat from "react-number-format";
import { roundDisplayPrice } from "../../utils/priceHelpers";
import { showSuccessNotification } from "../../common/utils/notification";

const PendingActivitiesModal = ({ visible, onClose, activeOrders = [], onViewOrder, onRefresh }) => {
  const handleItemViewOrder = (order) => {
    if (typeof onRefresh === "function") {
      onRefresh();
    }
    if (onClose) onClose();
    if (onViewOrder) onViewOrder(order);
  };

  const handleCopyCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    showSuccessNotification("Collection code copied to clipboard!");
  };

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      if (typeof onRefresh === "function") {
        onRefresh();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [visible, onRefresh]);

  const safeOrders = Array.isArray(activeOrders)
    ? activeOrders
    : Array.isArray(activeOrders?.orders)
      ? activeOrders.orders
      : [];

  const waitingOrders = safeOrders.filter(
    (o) =>
      o.status === "WAITING" ||
      o.status === "ACCEPTED" ||
      o.status === "pending" ||
      o.delivery_status === "pending" ||
      o.delivery_status === "accepted"
  );
  const collectionOrders = safeOrders.filter(
    (o) =>
      (o.status === "READY" || o.delivery_status === "ready") &&
      o.status !== "DELIVERED" &&
      o.status !== "COMPLETED"
  );
  const inTransitOrders = safeOrders.filter(
    (o) =>
      o.status === "IN_TRANSIT" ||
      o.status === "PICKED" ||
      o.delivery_status === "in_transit" ||
      o.delivery_status === "picked"
  );

  const availableTabs = [
    { key: "waiting", label: "Waiting", count: waitingOrders.length, icon: <ClockCircleOutlined /> },
    { key: "collection", label: "For Collection", count: collectionOrders.length, icon: <ShoppingOutlined /> },
    { key: "in-transit", label: "In-Transits", count: inTransitOrders.length, icon: <CarOutlined /> },
  ];

  const hasItems = waitingOrders.length > 0 || collectionOrders.length > 0 || inTransitOrders.length > 0;
  const [activeTab, setActiveTab] = useState("waiting");

  useEffect(() => {
    if (waitingOrders.length > 0) {
      setActiveTab("waiting");
    } else if (collectionOrders.length > 0) {
      setActiveTab("collection");
    } else if (inTransitOrders.length > 0) {
      setActiveTab("in-transit");
    }
  }, [safeOrders.length]);

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <ClockCircleOutlined className="text-gray-800 text-lg" />
          <span className="font-gilroyBold text-base sm:text-lg text-gray-900">Pending Activities</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      centered
      footer={[
        <Button key="close" type="primary" onClick={onClose} className="bg-black hover:bg-gray-800 text-white border-none px-5 rounded-xl h-10 text-[14px] font-gilroyBold">
          Close
        </Button>,
      ]}
      width="92%"
      style={{ maxWidth: "520px" }}
      bodyStyle={{ maxHeight: "75vh", overflowY: "auto" }}
    >
      <div className="pt-1 space-y-3">
        {!hasItems ? (
          <Empty description="No pending activities at the moment" className="my-8" />
        ) : (
          <>
            {availableTabs.length > 0 && (
          <div
            className="flex gap-2 border-b border-gray-200 pb-2.5 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-2 rounded-xl text-[13px] sm:text-[14px] font-gilroyBold whitespace-nowrap transition-all flex items-center gap-1.5 shrink-0 ${
                  activeTab === tab.key
                    ? "bg-black text-white border border-black"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tab.icon}
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        )}

        {activeTab === "waiting" && (
          <div className="space-y-3">
            {waitingOrders.length > 0 ? (
              waitingOrders.map((order, idx) => (
                <div key={idx} className="p-3.5 sm:p-4 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-2.5">
                  <div className="flex items-center justify-between gap-1.5 border-b border-gray-200 pb-2">
                    <span className="text-[12px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      ORDER CODE / PICKUP CODE
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>

                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-1.5 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>

                  <div className="flex justify-between items-center text-[13px] sm:text-[14px] pt-0.5">
                    <div>
                      <span className="text-gray-500 font-gilroyBold">Items: </span>
                      <span className="font-gilroyBold text-gray-900">{order.total_items || order.items?.length || 1}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 font-gilroyBold">Total: </span>
                      <span className="font-gilroyBold text-gray-900">
                        <NumberFormat
                          thousandSeparator={true}
                          prefix={"₦"}
                          className="numeric"
                          displayType={"text"}
                          value={roundDisplayPrice(order.total_amount || order.totalAmount || 0)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-gray-500 text-[14px] font-gilroyBold">No waiting orders at the moment.</div>
            )}
          </div>
        )}

        {activeTab === "collection" && (
          <div className="space-y-3">
            {collectionOrders.length > 0 ? (
              collectionOrders.map((order, idx) => {
                const collectionCode = order.pickupCode || order.orderCode || order.transaction_ref || order.id || "N/A";
                return (
                  <div key={idx} className="p-3.5 sm:p-4 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-2.5">
                    <div className="flex items-center justify-between gap-1.5 border-b border-gray-200 pb-2">
                      <span className="text-[12px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                        READY FOR COLLECTION
                      </span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => handleItemViewOrder(order)}
                        className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                      >
                        View
                      </Button>
                    </div>

                    <div className="flex items-center justify-between gap-2 my-1.5">
                      <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest break-all">
                        {collectionCode}
                      </div>
                      <Button
                        type="default"
                        icon={<CopyOutlined className="text-gray-700 text-base" />}
                        onClick={() => handleCopyCode(collectionCode)}
                        className="flex items-center justify-center border border-gray-300 rounded-xl px-3 py-1 text-[13px] font-gilroyBold hover:bg-gray-100 shrink-0"
                        title="Copy Collection Code"
                      >
                        Copy
                      </Button>
                    </div>

                    <div className="text-[13px] sm:text-[14px] text-gray-700 pt-0.5">
                      Your order is ready at the vendor location. Please present your COLLECTION CODE upon arrival.
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="py-6 text-center text-gray-500 text-[14px] font-gilroyBold">No orders ready for collection.</div>
            )}
          </div>
        )}

        {activeTab === "in-transit" && (
          <div className="space-y-3">
            {inTransitOrders.length > 0 ? (
              inTransitOrders.map((order, idx) => (
                <div key={idx} className="p-3.5 sm:p-4 border border-gray-200 rounded-2xl bg-gray-50/50 space-y-2.5">
                  <div className="flex items-center justify-between gap-1.5 border-b border-gray-200 pb-2">
                    <span className="text-[12px] text-gray-500 font-gilroyBold uppercase tracking-wider">
                      IN-TRANSIT DELIVERY
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleItemViewOrder(order)}
                      className="bg-blue-600 hover:bg-blue-700 text-white border-none rounded-xl px-4 py-1 text-[13px] font-gilroyBold shrink-0"
                    >
                      View
                    </Button>
                  </div>

                  <div className="font-gilroyBold text-3xl sm:text-4xl text-black font-black tracking-widest my-1.5 break-all">
                    {order.orderCode || order.transaction_ref || order.id || "N/A"}
                  </div>

                  <div className="text-[13px] sm:text-[14px] text-gray-700 pt-0.5">
                    Your delivery rider is currently en route to your address.
                  </div>
                </div>
              ))
            ) : (
              <div className="py-6 text-center text-gray-500 text-[14px] font-gilroyBold">No orders currently in-transit.</div>
            )}
          </div>
        )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default PendingActivitiesModal;
